import { useMutation, useQuery } from "@tanstack/react-query";
import { getGRNReportEnquiry, exportGRNReportAllCustomer } from "services/data.report.service";
import { TableCard } from "ui-components/Layout";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { GRNReportEnquiryColumns } from "./columns";
import { useState, useEffect } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { CSVLink } from "react-csv";
import { useTeamValue } from "providers/TeamContext";
import dayjs from "dayjs";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import { Grid } from "@mui/material";
import { LoadingDialog, Success, Error, Error_ } from "ui-components/Alert";
import { EmailButton } from "ui-components/Button";
import { ExportButton } from "ui-components/Button";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";
import useFileDownloadHook from "hooks/useFileDownloadHook";

function GRNReportEnquiry() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const exportPermission = allowAll || (Boolean(permissions["GRN_Report"]) && permissions["GRN_Report"].findIndex(b => b === "export") !== -1);
  const date = dayjs(new Date()).format("YYYY-MM-DD");
  const sevenDaysBefore = dayjs(new Date()).subtract(1, 'day').format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(sevenDaysBefore);
  const [endDate, setEndDate] = useState(date);
  const customProps = { startDate, setStartDate, endDate, setEndDate };
  const [alert, setAlert] = useState(null);
  const { downloadCsv, isDownloading } = useCsvDownloadHook();
  const { downloadFile, isDownloading: isExporting, error: exportError } = useFileDownloadHook();

  useEffect(() => {
    if (exportError) {
      setAlert(<Error_ content={exportError.message} onChange={setAlert} />);
    }
  }, [exportError]);

  const validateDateRange = () => {
    setAlert(null);
    const valid =
      dayjs(endDate).isSame(dayjs(startDate)) ||
      dayjs(endDate).isAfter(dayjs(startDate));
    if (!valid) {
      setAlert(<Error_ content="Start date cannot greater than end date" onChange={setAlert} />);
      return false;
    }
    if (dayjs(date).isBefore(endDate)) {
      setAlert(<Error_ content="End date cannot greater than today date" onChange={setAlert} />);
      return false;
    }
    return true;
  };

  const onFetch = () => {
    if (!validateDateRange()) return;
    refetch();
  };

  const onExportAllCustomer = () => {
    if (!validateDateRange()) return;
    const filename = `ALLCUSTOMER_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_GRNReport.xlsx`;
    downloadFile(() => exportGRNReportAllCustomer({ startDate, endDate }), filename);
  };

  const { refetch, isFetching } = useQuery({
    queryKey: ["grn-report-enquiry", { customerCode }],
    queryFn: () => getGRNReportEnquiry({ customerCode, startDate, endDate }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => {
      setData(data.data.data);
      setFilteredData(data.data.data);
    },
    onError: () => { },
  });

  const headers = [
    { label: "Customer Code", key: "customerCode" },
    { label: "Recv Code", key: "recvCode" },
    { label: "Ext Recv Code", key: "extRecvCode" },
    { label: "Vendor Code", key: "vendorCode" },
    { label: "PO Number", key: "poNumber" },
    { label: "ASN Code", key: "asnCode" },
    { label: "PO Remark", key: "poRemark" },
    { label: "Receipt Date", key: "receiptDate" },
    { label: "Putaway Start", key: "putawayStart" },
    { label: "Putaway End", key: "putawayEnd" },
    { label: "Chargeable", key: "chargeable" },
    { label: "Status", key: "status" },
    { label: "Line No", key: "recvLineNo" },
    { label: "SKU Code", key: "recvSKUCode" },
    { label: "Category", key: "category" },
    { label: "SKU Descriptions", key: "descriptions1" },
    { label: "Pack Type", key: "packType" },
    { label: "V DO No", key: "vdoNo" },
    { label: "V Inv No", key: "vInvNo" },
    { label: "OLN", key: "oln" },
    { label: "MLN", key: "mln" },
    { label: "Manual Lot", key: "manualLot" },
    { label: "Carton ID", key: "cartonID" },
    { label: "Manufacturing Date", key: "manufacturingDate" },
    { label: "Expiry Date", key: "expiryDate" },
    { label: "Quantity Expected", key: "qtyExpected" },
    { label: "Quantity Received", key: "qtyReceived" },
    { label: "UOM", key: "uom" },
    { label: "Zone Id", key: "toZone" },
    { label: "Location Id", key: "toLocation" },
    { label: "Location Type", key: "locationType" },
    { label: "Cubic UPM", key: "upmCubic" },
    { label: "Total Cubic UPM", key: "totalUPMCubic" },
    { label: "Case UPM", key: "upmCase" },
    { label: "Total Case UPM", key: "totalUPMCases" },
    { label: "Putaway No", key: "putawayNumber" },
    { label: "Putaway By", key: "putawayBy" },
    { label: "Created On", key: "createdOn" },
    { label: "Updated On", key: "updatedOn" },
  ];

  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };

  const onSuccess = (data) => {
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => {
    setAlert(<Error data={data} onChange={setAlert} />);
  };

  return (
    <ListLayout>
      {isFetching && <LoadingDialog />}
      {(isDownloading || isExporting) && <ExportDialog />}
      {alert && <MDBox sx={{ my: 3 }}>{alert}</MDBox>}
      <TableCard sx={{ my: 3 }}>
        <MDBox margin="1rem">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MDBox sx={{ width: { xs: "100%", sm: "50%" } }}>
                <EmailButton
                  variant="gradient"
                  color="info"
                  filename={`${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_GRNReport`}
                  fileData={filteredData}
                  onSuccess={onSuccess}
                  onError={onError}
                  customerCode={customerCode}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} sm={4}>
              <CustomDateRangePicker {...customProps} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDBox display="flex" flexWrap="wrap" alignItems="center" sx={{ gap: "1rem" }}>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => {
                    onFetch();
                  }}
                >
                  search
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={!exportPermission}
                  onClick={() => {
                    const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_GRNReport`;
                    downloadCsv(filename, filteredData)
                  }}
                >
                  export
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={!exportPermission}
                  onClick={() => {
                    onExportAllCustomer();
                  }}
                >
                  export all customer
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        {/* <MDBox display="flex" justifyContent="flex-end" margin="10px">
          <MDButton variant="gradient" color="info" disabled={!exportPermission}>
            <CSVLink style={{ color: "white" }} filename={`GRN_Report`} headers={headers} data={filteredData}>
              export
            </CSVLink>
          </MDButton>
        </MDBox> */}
        <BasicTable
          data={data}
          columns={GRNReportEnquiryColumns}
          onFilterRowsChange={handleFilterRowsChange}
        />
      </TableCard>
    </ListLayout>
  );
}

export default GRNReportEnquiry;
